export interface ChatMessage {
  id: string;
  content: string;
  role: "user" | "assistant" | "test";
  timestamp: Date;
  sources?: SourceDocument[];
  error?: string;
  isStreaming?: boolean;
  logs?: AgentStreamUpdate[];
  status?: string;
  progress?: number;
  taskId?: string;
}

export interface SourceDocument {
  document_id: string;
  document: string;
  content: string;
  file_info: Record<string, any>;
}

export interface TemplateTag {
  id: string;
  name: string;
  color: string;
  description?: string;
}

export interface TagAssociatedDocument {
  id: string;
  filename: string;
}

export interface FeedbackContent {
  chat_id: string;
  section: Record<string, any>;
  feedback: string;
}

export interface RegenerateContent {
  chat_id: string;
  section: Record<string, any>;
  freeform: string;
  options: string[];
}

export interface QualityCheckContent {
  name: string;
  value: number;
  description?: string;
}

export interface QualityCheckData {
  score: number;
  reason?: string;
}

export interface ErrorUI {
  isVisible: boolean;
  type: string;
  title: string;
  message: string;
  details: ErrorUIDetails;
}

interface ErrorUIDetails {
  message: string;
  code: string;
  stack: string;
  context: Record<string, any>;
}

export interface EvalStatus {
  [sectionId: string]: EvalStatusTasks;
}

interface EvalStatusTasks {
  multimodel: EvalStatusSubTask;
}

export interface EvalStatusSubTask {
  status: Status;
  data: QualityCheckData | null;
}

export type Status = "uninitialized" | "pending" | "completed" | "unavailable";

export interface UploadedFile {
  id: string;
  sg_id: string;
  doc_id: string;
  tag_id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  content?: string;
  uploadProgress: number;
  error?: string;
}

export interface FontStyleSettings {
  fontFamily: FontFamily;
  fontSize: number;
  fontColor: string;
  bold?: boolean;
  italic?: boolean;
  highlightColor: string | null;
}

export type FontStyleSettingsType = "draftStyles" | "confirmedStyles" | "userStyles";

export const fontFamilies = [
  "Andale Mono",
  "Arial",
  "Arial Black",
  "Arial Narrow",
  "Baskerville",
  "Bodoni",
  "Book Antiqua",
  "Bookman Old Style",
  "Century Gothic",
  "Century Schoolbook",
  "Chalkboard",
  "Copperplate",
  "Courier",
  "Courier New",
  "Didot",
  "Franklin Gothic",
  "Futura",
  "Garamond",
  "Georgia",
  "Gill Sans",
  "Hoefler Text",
  "Impact",
  "Lucida Console",
  "Menlo",
  "Monaco",
  "Optima",
  "Palatino",
  "Palatino Linotype",
  "Rockwell",
  "Tahoma",
  "Times New Roman",
  "Trebuchet MS",
  "Verdana",
];

export type FontFamily = (typeof fontFamilies)[number];

// ────────────────────────────────────────────
// Chat History (Backend Response Shape)
// ────────────────────────────────────────────
export interface ChatHistory {
  id: string;
  question: string;
  answer: string;
  title?: string;
  is_pinned: boolean;
  created_at: string;
  sources?: SourceDocument[];
  previous_chat_history_id?: string | null;
}

// ────────────────────────────────────────────
// Deep Thinking / Agent Types
// ────────────────────────────────────────────
export type ThinkMode = "lite" | "deep";

export interface AgentStreamUpdate {
  status: string;
  message?: string;
  progress?: number;
  timestamp: number;
}

export interface AgentTaskStatus {
  status: string;
  message?: string;
  progress?: number;
  answer?: string;
  response_data?: {
    answer?: string;
    sources?: Array<{
      filename: string;
      score: number;
      content: string;
      file_id?: string;
    }>;
  };
  error?: string;
  trace_url?: string;
}

// ────────────────────────────────────────────
// Extended ChatMessage for Deep Thinking
// ────────────────────────────────────────────
export interface ExtendedChatMessage extends ChatMessage {
  sources?: SourceDocument[];
  error?: string;
  isStreaming?: boolean;
  logs?: AgentStreamUpdate[];
  status?: string;
  progress?: number;
  taskId?: string;
}

// ────────────────────────────────────────────
// Model & Tag Types
// ────────────────────────────────────────────
export interface ModelEntry {
  id: string;
  display_name: string;
  deployment: string;
  is_agentic: boolean;
  is_enabled: boolean;
}

export interface Tag {
  id: string;
  name: string;
  color?: string;
  description?: string;
}
